import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkChangeWorkItemCustomerApiV1SoftwareFactoryWorkItemsBulkCustomersPost,
  bulkUpdateWorkItemsApiV1SoftwareFactoryWorkItemsBulkUpdatePost,
  bulkWorkItemCustomerSummaryApiV1SoftwareFactoryWorkItemsBulkCustomersSummaryPost,
  getWorkItemFilterFacetsApiV1SoftwareFactoryWorkItemsFilterFacetsGet,
} from '../../../generated/api/software-factory/software-factory';
import type { WorkItemBulkCustomerRequest } from '../../../generated/api/models/workItemBulkCustomerRequest';
import type { WorkItemBulkUpdateRequest } from '../../../generated/api/models/workItemBulkUpdateRequest';
import { workItemApi } from '../services/softwareFactoryApi';
import type { ListResponse, ListWorkItemsParams, SfWorkItem, SfWorkItemAiSetupWrite, SfWorkItemCreate, SfWorkItemUpdate, WorkItemTransitionData } from '../types';

export const workItemKeys = {
  all: ['sdevWorkItems'] as const,
  list: (params?: ListWorkItemsParams) => [...workItemKeys.all, 'list', params] as const,
  detail: (id: string) => [...workItemKeys.all, 'detail', id] as const,
  transitions: (id: string) => [...workItemKeys.all, 'transitions', id] as const,
  cycles: (id: string) => [...workItemKeys.all, 'cycles', id] as const,
  commits: (id: string) => [...workItemKeys.all, 'commits', id] as const,
  customers: (id: string) => [...workItemKeys.all, 'customers', id] as const,
  children: (id: string) => [...workItemKeys.all, 'children', id] as const,
  aiSetups: (id: string) => [...workItemKeys.all, 'aiSetups', id] as const,
  dispatchable: ['sdevWorkItems', 'dispatchable'] as const,
  filterFacets: (adminScope?: string) =>
    [...workItemKeys.all, 'filterFacets', adminScope ?? null] as const,
};

/**
 * Candidate values for the list's entity-FK filters — the modules, initiatives,
 * assignees and customer organizations actually in use on ≥1 work item this caller
 * can see, with counts (`docs/design-system/ui-list-pages.md` § Filter Option
 * Sources).
 *
 * `adminScope` is in the key and NOTHING ELSE is, deliberately: the facet is
 * computed over the list's BASE scope, so it must not be refetched as sibling
 * filters change — a facet that jitters while a query is composed makes filter
 * order significant and hides the value the user is switching TO. The admin scope
 * is not a filter but a different base set, which is why it belongs here.
 *
 * `staleTime` is generous for the same reason: the option set moves when work
 * items are created or re-linked, not while the toolbar is being used.
 */
export function useWorkItemFilterFacets(adminScope?: string) {
  return useQuery({
    queryKey: workItemKeys.filterFacets(adminScope),
    queryFn: () =>
      getWorkItemFilterFacetsApiV1SoftwareFactoryWorkItemsFilterFacetsGet(
        adminScope ? { admin_scope: adminScope } : undefined,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkItemChildren(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workItemKeys.children(id),
    queryFn: () => workItemApi.listChildren(id),
    enabled: !!id && (options?.enabled ?? true),
  });
}

// Search and filters are server-side and part of the query key, so every
// keystroke is a new query. `keepPreviousData` holds the current rows on screen
// while the next result loads — without it the list falls back to `isPending`
// and the page's toolbar unmounts mid-typing. See ui-list-pages.md § 2.
export function useWorkItems(params?: ListWorkItemsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: workItemKeys.list(params),
    queryFn: () => workItemApi.list(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useWorkItem(id: string) {
  return useQuery({
    queryKey: workItemKeys.detail(id),
    queryFn: () => workItemApi.get(id),
    enabled: !!id,
  });
}

export function useCreateWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SfWorkItemCreate) => workItemApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
    },
  });
}

export function useUpdateWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SfWorkItemUpdate }) => workItemApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
      // A sprint_id change moves the item between the backlog and a sprint board,
      // so refresh the sprint caches too (mirrors useAssignWorkItemToSprint). Keyed
      // by the sprintKeys.all prefix, inlined to avoid a circular hook-module import.
      if (variables.data.sprint_id !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['sdevSprints'] });
      }
    },
  });
}

/**
 * Apply one set of scalar fields (priority / autonomy / module / initiative /
 * assignee) to a selection of work items — the loaded page, or every row matching
 * the list's filter set.
 *
 * The endpoint extends `EscalatableBulkRequest`, so `ids` is ABSENT on an
 * escalated call; the per-item invalidation below is therefore optional rather
 * than assumed. It is also redundant — `workItemKeys.all` is a prefix of
 * `detail(id)`, so the first invalidation already covers them — and kept only
 * because an explicit key is what a reader looks for.
 */
export function useBulkUpdateWorkItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkItemBulkUpdateRequest) =>
      bulkUpdateWorkItemsApiV1SoftwareFactoryWorkItemsBulkUpdatePost(data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      for (const id of variables.ids ?? []) {
        queryClient.invalidateQueries({ queryKey: workItemKeys.detail(id) });
      }
    },
  });
}

/**
 * Link or unlink ONE customer reference across a selection of work items — the
 * loaded page, or every row matching the list's filter set.
 *
 * Invalidates each item's `customers` key as well as the lists: the detail page's
 * Customers section reads that key, and a bulk apply is exactly when it goes stale
 * without anything on that page having been touched. On an ESCALATED call there is
 * no id list to walk — `workItemKeys.all` is a prefix of `customers(id)`, so the
 * first invalidation is what covers those rows.
 */
export function useBulkChangeWorkItemCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkItemBulkCustomerRequest) =>
      bulkChangeWorkItemCustomerApiV1SoftwareFactoryWorkItemsBulkCustomersPost(data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      for (const id of variables.ids ?? []) {
        queryClient.invalidateQueries({ queryKey: workItemKeys.customers(id) });
      }
    },
  });
}

/**
 * How many of a selection already carry a given customer reference.
 *
 * A POST that reads, because the id list is the request body — so it is modelled
 * as a query keyed on that body rather than a mutation, and deliberately kept
 * OUT of the `workItemKeys` prefix: it must not be invalidated (and refetched)
 * by the very apply whose result changes it. `staleTime: 0` / `gcTime: 0` means
 * reopening the dialog always asks again.
 */
export function useWorkItemCustomerCoverage(
  body: WorkItemBulkCustomerRequest | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['sdevWorkItemCustomerCoverage', body],
    enabled: (options?.enabled ?? true) && body !== null,
    staleTime: 0,
    gcTime: 0,
    queryFn: () =>
      bulkWorkItemCustomerSummaryApiV1SoftwareFactoryWorkItemsBulkCustomersSummaryPost(
        body as WorkItemBulkCustomerRequest,
      ),
  });
}

/**
 * Reschedule a work item's planned span (start_date / end_date) with an
 * optimistic patch across every cached work-item list, rolling back on error.
 * Used by the Roadmap Gantt's drag-to-reschedule interaction — the optimistic
 * update lets the bar settle at its new position before the server confirms.
 * Dates are ISO `yyyy-MM-dd` strings (the API's planning-date format).
 */
export function useRescheduleWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, start_date, end_date }: { id: string; start_date: string; end_date: string }) =>
      workItemApi.update(id, { start_date, end_date }),
    onMutate: async ({ id, start_date, end_date }) => {
      await queryClient.cancelQueries({ queryKey: workItemKeys.all });
      const snapshots = queryClient.getQueriesData<ListResponse<SfWorkItem>>({ queryKey: workItemKeys.all });
      for (const [key, data] of snapshots) {
        if (!data?.items) continue;
        queryClient.setQueryData<ListResponse<SfWorkItem>>(key, {
          ...data,
          items: data.items.map((w) => (w.id === id ? { ...w, start_date, end_date } : w)),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      // The global mutation-error handler shows the specific translated toast.
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
    },
  });
}

export function useDeleteWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
    },
  });
}

export function useUpdateWorkItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => workItemApi.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
    },
  });
}

export function useRebaselineWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.rebaseline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(id) });
    },
  });
}

export function useWorkItemTransitions(id: string) {
  return useQuery({
    queryKey: workItemKeys.transitions(id),
    queryFn: () => workItemApi.listTransitions(id),
    enabled: !!id,
  });
}

/**
 * The item's rework cycles, oldest first. One row per pass at the item — a
 * reopen opens a new one — so a status transition invalidates this alongside
 * the transition log.
 */
export function useWorkItemCycles(id: string) {
  return useQuery({
    queryKey: workItemKeys.cycles(id),
    queryFn: () => workItemApi.listCycles(id),
    enabled: !!id,
  });
}

export function useTransitionWorkItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkItemTransitionData }) =>
      workItemApi.transitionStatus(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: workItemKeys.transitions(variables.id) });
      // A terminal transition closes the current cycle and a reopen opens a new
      // one, so the cycle list is stale the moment the status moves.
      queryClient.invalidateQueries({ queryKey: workItemKeys.cycles(variables.id) });
    },
  });
}

export function useLinkedTicket(workItemId: string) {
  return useQuery({
    queryKey: [...workItemKeys.all, 'linked-ticket', workItemId] as const,
    queryFn: () => workItemApi.getLinkedTicket(workItemId),
    enabled: !!workItemId,
  });
}

export function useWorkItemCommits(id: string) {
  return useQuery({
    queryKey: workItemKeys.commits(id),
    queryFn: () => workItemApi.getCommits(id),
    enabled: !!id,
  });
}

export function useLinkCommits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shas }: { id: string; shas: string[] }) => workItemApi.linkCommits(id, shas),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.commits(variables.id) });
    },
  });
}

export function useVoteOnWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workItemApi.vote(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(id) });
    },
  });
}

export function useWorkItemCustomers(id: string) {
  return useQuery({
    queryKey: workItemKeys.customers(id),
    queryFn: () => workItemApi.listCustomers(id),
    enabled: !!id,
  });
}

export function useAddWorkItemCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      organization_id,
      contact_id,
      deal_id,
    }: {
      id: string;
      organization_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
    }) => workItemApi.addCustomer(id, { organization_id, contact_id, deal_id }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(variables.id) });
    },
  });
}

export function useRemoveWorkItemCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      workItemApi.removeCustomer(id, customerId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.customers(variables.id) });
    },
  });
}

/**
 * Replace a work item's AI setups with an ordered list.
 *
 * Also invalidates the item detail: the write re-projects the item's
 * preferred_provider / preferred_model / preferred_thinking_level from entry 0, so the
 * detail cache is stale the moment this succeeds.
 */
export function useReplaceWorkItemAiSetups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: SfWorkItemAiSetupWrite[] }) =>
      workItemApi.replaceAiSetups(id, items),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workItemKeys.aiSetups(variables.id) });
      queryClient.invalidateQueries({ queryKey: workItemKeys.detail(variables.id) });
    },
  });
}
